/**
 * iter138 — useSessionPresence hook (3-tier live presence).
 *
 *   const { online, viewing, considering, markConsidering } =
 *     useSessionPresence(sessionId);
 *
 * • online        — Set<user_id> of group members with an SSE stream open
 * • viewing       — array of { user_id, user_name } currently on this page
 * • considering   — array of { user_id, user_name } hovering an RSVP button
 * • markConsidering(durationMs?) — call on hover/focus of RSVP buttons
 *
 * Lifecycle:
 *   • On mount: sends `state="viewing"` immediately, then every 15s.
 *   • On `markConsidering()`: sends `state="considering"`, refreshes every 3s
 *     until 3s after the last invocation, then auto-reverts to "viewing".
 *   • On unmount / tab-hide: sends `state="left"`.
 *   • Subscribes to `session_presence_changed` SSE event to keep `viewing` /
 *     `considering` lists fresh in real time across all viewers.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useRealtime } from '../context/RealtimeContext';

const VIEW_HEARTBEAT_MS = 15000;
const CONSIDER_HEARTBEAT_MS = 3000;
const CONSIDER_GRACE_MS = 3000;

export default function useSessionPresence(sessionId, { enabled = true } = {}) {
  const [online, setOnline] = useState(() => new Set());
  const [viewing, setViewing] = useState([]);
  const [considering, setConsidering] = useState([]);
  const { onEvent } = useRealtime();

  const stateRef = useRef('viewing');
  const considerUntilRef = useRef(0);
  const beatTimerRef = useRef(null);
  const leftSentRef = useRef(false);

  const send = useCallback(async (state) => {
    if (!sessionId) return;
    try {
      await api.post(`/sessions/${sessionId}/presence`, { state });
    } catch {
      /* presence is best-effort */
    }
  }, [sessionId]);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/sessions/${sessionId}/presence`);
      setViewing(r.data?.viewing || []);
      setConsidering(r.data?.considering || []);
      setOnline(new Set(r.data?.online_user_ids || []));
    } catch {
      /* ignore */
    }
  }, [sessionId]);

  const tick = useCallback(() => {
    const now = Date.now();
    const next =
      considerUntilRef.current > now ? 'considering' : 'viewing';
    stateRef.current = next;
    send(next);
  }, [send]);

  const scheduleNext = useCallback(() => {
    if (beatTimerRef.current) clearTimeout(beatTimerRef.current);
    const interval =
      stateRef.current === 'considering'
        ? CONSIDER_HEARTBEAT_MS
        : VIEW_HEARTBEAT_MS;
    beatTimerRef.current = setTimeout(() => {
      tick();
      scheduleNext();
    }, interval);
  }, [tick]);

  const markConsidering = useCallback(() => {
    considerUntilRef.current = Date.now() + CONSIDER_GRACE_MS;
    if (stateRef.current !== 'considering') {
      stateRef.current = 'considering';
      send('considering');
      scheduleNext();
    }
  }, [send, scheduleNext]);

  // Main lifecycle: heartbeat + leave + visibility
  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    leftSentRef.current = false;
    refresh();
    tick();
    scheduleNext();

    const onVisibility = () => {
      if (document.hidden) {
        if (beatTimerRef.current) clearTimeout(beatTimerRef.current);
        if (!leftSentRef.current) {
          leftSentRef.current = true;
          send('left');
        }
      } else {
        leftSentRef.current = false;
        tick();
        scheduleNext();
      }
    };
    const onPagehide = () => {
      if (!leftSentRef.current) {
        leftSentRef.current = true;
        // Use sendBeacon for reliability on tab close
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sessionId}/presence`;
          const blob = new Blob([JSON.stringify({ state: 'left' })],
                                { type: 'application/json' });
          // sendBeacon can't set auth headers; fall back to fire-and-forget POST.
          // Best-effort: most browsers also flush in-flight fetch on pagehide.
          if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
        } catch { /* noop */ }
        send('left');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPagehide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPagehide);
      if (beatTimerRef.current) clearTimeout(beatTimerRef.current);
      if (!leftSentRef.current) send('left');
    };
  }, [enabled, sessionId, send, tick, scheduleNext, refresh]);

  // Subscribe to SSE presence events
  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    return onEvent('session_presence_changed', (payload) => {
      if (payload?.session_id !== sessionId) return;
      refresh();
    });
  }, [enabled, sessionId, onEvent, refresh]);

  return { online, viewing, considering, markConsidering };
}
